/**
 * LifecycleLogsTab — Cloud-only event lifecycle logs table.
 *
 * Features: search filter, sortable columns, CSV export.
 *
 * Props:
 *   logs      — array of log records
 *   loading   — boolean
 *   tzOffset  — timezone offset for datetime display
 *   eventId   — event ID (for CSV filename)
 */

import { useState, useMemo, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';

const COLUMNS = [
    { key: 'create_time', label: 'Timestamp', sortable: true },
    { key: 'log_type', label: 'Operation', sortable: true },
    { key: 'message_status', label: 'Last Status', sortable: true },
    { key: 'contact_info', label: 'Details', sortable: true },
];

const CSV_HEADERS = [
    { label: 'Timestamp', key: 'create_time' },
    { label: 'Operation', key: 'log_type' },
    { label: 'Details', key: 'contact_info' },
];

const LifecycleLogsTab = ({ logs = [], loading, tzOffset, eventId }) => {
    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState('create_time');
    const [sortOrder, setSortOrder] = useState('asc');

    const handleSort = useCallback((key) => {
        if (sortColumn === key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(key);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    // Filter by search
    const filtered = useMemo(() => {
        if (!search.trim()) return logs;
        const q = search.toLowerCase();
        return logs.filter((row) =>
            COLUMNS.some((col) => {
                const v = row[col.key];
                return v != null && String(v).toLowerCase().includes(q);
            })
        );
    }, [logs, search]);

    // Sort
    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            const aVal = a[sortColumn] || '';
            const bVal = b[sortColumn] || '';
            const cmp = String(aVal).localeCompare(String(bVal));
            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return arr;
    }, [filtered, sortColumn, sortOrder]);

    // CSV filename
    const csvFilename = useMemo(() => {
        const idPart = eventId ? String(eventId).split('@')[1] || eventId : 'event';
        return `${idPart}_lifecycle_logs.csv`;
    }, [eventId]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                Loading lifecycle logs...
            </div>
        );
    }

    return (
        <div>
            {/* Controls */}
            <div className="ep-notes-controls">
                <div className="ep-notes-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search logs..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="ap-search__input"
                            aria-label="Search logs"
                        />
                    </div>
                </div>
                <div className="ep-notes-controls__right">
                    {sorted.length > 0 ? (
                        <CSVLink
                            data={sorted}
                            headers={CSV_HEADERS}
                            filename={csvFilename}
                            className="rp-btn rp-btn--secondary"
                            style={{ textDecoration: 'none' }}
                        >
                            <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                        </CSVLink>
                    ) : null}
                </div>
            </div>

            {/* Table */}
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            {COLUMNS.map((col) => (
                                <th key={col.key} className="ap-th">
                                    <div className="ap-th__content">
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => handleSort(col.key)}
                                                className="ap-th__sort-btn"
                                            >
                                                {col.label}
                                                {sortColumn === col.key ? (
                                                    <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                        {sortOrder === 'asc'
                                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                        }
                                                    </svg>
                                                ) : null}
                                            </button>
                                        ) : (
                                            <span>{col.label}</span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td" colSpan={COLUMNS.length} style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No lifecycle logs available.</span>
                                </td>
                            </tr>
                        ) : null}
                        {sorted.map((row, idx) => (
                            <tr key={idx} className="dp-tr">
                                <td className="dp-td">
                                    <span className="dp-cell-text" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--font-size-xs)' }}>
                                        {row.create_time ? convertIsoUTCToLocal(row.create_time, tzOffset) : '\u2014'}
                                    </span>
                                </td>
                                <td className="dp-td">
                                    <span className="dp-cell-text">{row.log_type || '\u2014'}</span>
                                </td>
                                <td className="dp-td">
                                    <span className="dp-cell-text">{row.message_status || '\u2014'}</span>
                                </td>
                                <td className="dp-td">
                                    <span className="dp-cell-text">{row.contact_info || '\u2014'}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LifecycleLogsTab;
